<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  domain: { type: String, default: '' },
  contactsCount: { type: Number, default: 0 },
  avatarUrl: { type: String, default: '' },
  lastActivityAt: { type: [String, Number], default: null },
});

const emit = defineEmits(['showCompany']);

const { t } = useI18n();

const onClickViewDetails = () => emit('showCompany', props.id);

const displayName = computed(() => props.name || t('COMPANIES.UNNAMED'));

const avatarSource = computed(() => props.avatarUrl || null);

const hasContacts = computed(() => Number(props.contactsCount || 0) > 0);

const contactsCountLabel = computed(() =>
  t('COMPANIES.CONTACTS_COUNT', { n: Number(props.contactsCount || 0) })
);

const formattedLastActivityAt = computed(() => {
  if (!props.lastActivityAt) return '';
  return dynamicTime(props.lastActivityAt);
});
</script>

<template>
  <CardLayout
    layout="row"
    class="!rounded-none !bg-transparent !outline-none transition-colors hover:!bg-auth-soft/70 focus-within:!bg-auth-soft/70 dark:hover:!bg-auth-soft-dark/70 dark:focus-within:!bg-auth-soft-dark/70 [&>div:first-child]:!px-4 [&>div:first-child]:!py-4 sm:[&>div:first-child]:!px-5"
    @click="onClickViewDetails"
  >
    <div
      class="flex flex-1 cursor-pointer items-center justify-start gap-3 sm:gap-4"
    >
      <Avatar
        :username="displayName"
        :src="avatarSource"
        class="shrink-0"
        :name="name"
        :size="42"
        hide-offline-status
      />
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <div class="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
          <span
            class="truncate text-sm font-semibold text-auth-text dark:text-auth-text-dark sm:text-base"
          >
            {{ displayName }}
          </span>
          <span
            v-if="hasContacts"
            class="inline-flex truncate text-xs tabular-nums text-auth-muted dark:text-auth-muted-dark sm:items-center sm:gap-1.5 sm:text-sm"
          >
            <Icon
              icon="i-lucide-contact"
              size="size-3.5 text-auth-muted dark:text-auth-muted-dark"
              class="hidden sm:inline-flex"
            />
            {{ contactsCountLabel }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-3">
          <div class="flex min-w-0 items-center">
            <span
              v-if="domain"
              class="inline-flex cursor-text items-center gap-1.5 truncate text-sm text-auth-muted dark:text-auth-muted-dark"
              @click.stop
            >
              <Icon
                icon="i-lucide-globe"
                size="size-3.5 text-auth-muted dark:text-auth-muted-dark"
              />
              <span class="truncate">{{ domain }}</span>
            </span>
          </div>
          <span
            v-if="lastActivityAt"
            class="inline-flex flex-shrink-0 items-center gap-1.5 text-xs tabular-nums text-auth-muted dark:text-auth-muted-dark sm:text-sm"
          >
            {{ formattedLastActivityAt }}
          </span>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
